import type { ColumnDef } from '@tanstack/react-table'
import { CustomerTypeBadge } from '@/components/customers/CustomerTypeBadge'
import { Typography } from '@/components/ui/typography'
import dayjs from '@/lib/dayjs'
import type { CustomerBalanceReportCustomer } from '@/lib/services/reports/reports.types'
import { formatCurrency } from '@/lib/utils'
import { ArrowDownAZ, Building2, CalendarClock, DollarSign, ReceiptText } from 'lucide-react'

export function getCustomerBalanceReportColumns({
  formatPhone,
}: {
  formatPhone: (phone: string) => string
}): ColumnDef<CustomerBalanceReportCustomer>[] {
  return [
    {
      id: 'customer',
      accessorKey: 'name',
      header: 'Customer',
      cell: ({ row }) => {
        const customer = row.original

        return (
          <>
            <Typography
              variant="labelStrong"
              interaction="hoverUnderline"
              className="block truncate"
            >
              {customer.name || `Customer #${customer.id}`}
            </Typography>
            <Typography variant="caption" className="mt-1 block truncate">
              {[customer.email, customer.phone ? formatPhone(customer.phone) : null]
                .filter(Boolean)
                .join(' | ') || 'No contact info'}
            </Typography>
          </>
        )
      },
      meta: {
        label: 'Customer',
        icon: ArrowDownAZ,
        kind: 'primary',
        minWidth: '18rem',
        preferredWidth: 'minmax(18rem, 1fr)',
      },
    },
    {
      id: 'type',
      accessorKey: 'is_business',
      header: 'Type',
      cell: ({ row }) => <CustomerTypeBadge isBusiness={Boolean(row.original.is_business)} />,
      meta: {
        label: 'Type',
        icon: Building2,
        kind: 'badge',
        align: 'center',
      },
    },
    {
      id: 'last_activity_at',
      accessorKey: 'metrics.last_activity_at',
      header: 'Last Order',
      cell: ({ row }) => {
        const value = row.original?.metrics?.last_activity_at

        return value ? (
          <span className="whitespace-nowrap tabular-nums">
            {dayjs(value).format('MMM D, YYYY')}
          </span>
        ) : (
          <Typography variant="supporting">-</Typography>
        )
      },
      meta: {
        label: 'Last Order',
        icon: CalendarClock,
        kind: 'date',
        align: 'center',
        contentAlign: 'start',
      },
    },
    {
      id: 'open_invoice_count',
      accessorKey: 'open_invoice_count',
      header: 'Open Invoices',
      cell: ({ row }) => (
        <span className="font-medium tabular-nums">{row.original.open_invoice_count}</span>
      ),
      meta: {
        label: 'Open Invoices',
        mobileLabel: 'Open Invoices',
        icon: ReceiptText,
        kind: 'number',
        align: 'end',
      },
    },
    {
      id: 'open_balance_total',
      accessorKey: 'open_balance_total',
      header: 'Open Balance',
      cell: ({ row }) => (
        <span className="font-semibold tabular-nums">
          {formatCurrency(row.original.open_balance_total)}
        </span>
      ),
      meta: {
        label: 'Open Balance',
        mobileLabel: 'Open Balance',
        icon: DollarSign,
        kind: 'money',
        align: 'end',
      },
    },
    {
      id: 'overdue_balance_total',
      accessorKey: 'overdue_balance_total',
      header: 'Past Due',
      cell: ({ row }) => (
        <span className="font-medium tabular-nums">
          {formatCurrency(row.original.overdue_balance_total)}
        </span>
      ),
      meta: {
        label: 'Past Due',
        icon: CalendarClock,
        kind: 'money',
        align: 'end',
      },
    },
    {
      id: 'aging_current',
      accessorKey: 'aging.current',
      header: 'Current',
      cell: ({ row }) => formatCurrency(row.original.aging?.current ?? 0),
      meta: {
        label: 'Current',
        icon: CalendarClock,
        kind: 'money',
        align: 'end',
      },
    },
    {
      id: 'aging_1_30',
      accessorKey: 'aging.days_1_30',
      header: '1-30',
      cell: ({ row }) => formatCurrency(row.original.aging?.days_1_30 ?? 0),
      meta: {
        label: '1-30',
        icon: CalendarClock,
        kind: 'money',
        align: 'end',
      },
    },
    {
      id: 'aging_31_60',
      accessorKey: 'aging.days_31_60',
      header: '31-60',
      cell: ({ row }) => formatCurrency(row.original.aging?.days_31_60 ?? 0),
      meta: {
        label: '31-60',
        icon: CalendarClock,
        kind: 'money',
        align: 'end',
      },
    },
    {
      id: 'aging_61_90',
      accessorKey: 'aging.days_61_90',
      header: '61-90',
      cell: ({ row }) => formatCurrency(row.original.aging?.days_61_90 ?? 0),
      meta: {
        label: '61-90',
        icon: CalendarClock,
        kind: 'money',
        align: 'end',
      },
    },
    {
      id: 'aging_91_plus',
      accessorKey: 'aging.days_91_plus',
      header: '91+',
      cell: ({ row }) => formatCurrency(row.original.aging?.days_91_plus ?? 0),
      meta: {
        label: '91+',
        icon: CalendarClock,
        kind: 'money',
        align: 'end',
      },
    },
  ]
}
